import styled from '@emotion/styled';
import { colors } from '@innovationdepartment/proxima-ui';

// Layout Components
export const PageWrapper = styled.div`
  background-color: ${colors.neutral5};
  height: 100%;
  padding: 24px;
  display: flex;
  flex-direction: column;
  gap: 24px;
  overflow-y: auto;
`;

export const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

export const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

// Navigation
export const BackLink = styled.button`
  background: none;
  border: none;
  color: ${colors.blue50};
  cursor: pointer;
  font-size: 14px;
  text-decoration: none;
  padding: 0;

  &:hover {
    text-decoration: underline;
  }
`;

// Content Sections
export const ContentSection = styled.div`
  background-color: white;
  border: 1px solid ${colors.neutral20};
  border-radius: 8px;
  padding: 24px;
`;

export const SectionTitle = styled.h3`
  text-transform: uppercase;
  font-weight: 600;
  margin-bottom: 20px;
  letter-spacing: 0.5px;
  font-size: 12px;
  margin: 0 0 20px 0;
`;

// Form Layout
export const FormRow = styled.div`
  display: flex;
  gap: 24px;
  margin-bottom: 20px;
`;

export const FormField = styled.div<{ flex?: number }>`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: ${props => props.flex || 1};
`;

// Form Inputs
export const Label = styled.label<{ hasError?: boolean }>`
  font-weight: 600;
  font-size: 13px;
  color: ${props => (props.hasError ? colors.red50 : colors.neutral70)};
`;

export const StyledInput = styled.input<{ hasError?: boolean }>`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${props => (props.hasError ? colors.red50 : colors.neutral30)};
  border-radius: 4px;
  font-size: 14px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${props => (props.hasError ? colors.red50 : colors.blue50)};
  }
`;

export const StyledSelect = styled.select<{ hasError?: boolean }>`
  width: 100%;
  padding: 10px 12px;
  border: 1px solid ${props => (props.hasError ? colors.red50 : colors.neutral30)};
  border-radius: 4px;
  font-size: 14px;
  font-family: inherit;
  background-color: white;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: ${props => (props.hasError ? colors.red50 : colors.blue50)};
  }
`;

export const TextArea = styled.textarea<{ hasError?: boolean }>`
  padding: 10px 12px;
  border: 1px solid ${props => (props.hasError ? colors.red50 : colors.neutral30)};
  border-radius: 4px;
  font-size: 14px;
  font-family: inherit;
  resize: vertical;
  min-height: 80px;

  &:focus {
    outline: none;
    border-color: ${props => (props.hasError ? colors.red50 : colors.blue50)};
  }
`;

export const StyledCheckbox = styled.input`
  width: 16px;
  height: 16px;
  cursor: pointer;
`;

// Form Elements
export const CheckboxRow = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
`;

export const CheckboxLabel = styled.span`
  font-size: 14px;
`;

// Typography
export const Title = styled.h1`
  font-size: 20px;
  font-weight: 600;
  margin: 0;
`;

export const LoadingText = styled.div`
  font-size: 14px;
`;

// Table Components
export const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  background-color: white;
  border-radius: 8px;
  overflow: hidden;
  table-layout: fixed;
`;

export const TableHeader = styled.thead`
  background-color: ${colors.neutral10};
  position: sticky;
  top: 0;
  z-index: 1;
`;

export const TableHeaderCell = styled.th<{ sortable?: boolean }>`
  padding: 12px 16px;
  text-align: left;
  font-weight: 600;
  font-size: 12px;
  text-transform: uppercase;
  color: ${colors.neutral60};
  border-bottom: 2px solid ${colors.neutral20};
  cursor: ${props => (props.sortable ? 'pointer' : 'default')};
  user-select: none;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  min-width: 120px;
  width: 1%;

  &:hover {
    background-color: ${props => (props.sortable ? colors.neutral20 : 'transparent')};
  }
`;

export const SortIndicator = styled.span`
  margin-left: 4px;
  font-size: 10px;
`;

export const TableRow = styled.tr`
  cursor: pointer;
  border-bottom: 1px solid ${colors.neutral10};

  &:hover {
    background-color: ${colors.neutral5};
  }
`;

export const TableCell = styled.td`
  padding: 12px 16px;
  font-size: 14px;
  color: ${colors.neutral80};
  min-width: 120px;
  width: 1%;
`;
